import React, { useState } from 'react'
import SelectInput2 from '../Atoms/SelectInput2'
import FormField from '../Atoms/FormField'
import TextInput from '../Atoms/TextInput'
import RadioInput from '../Atoms/RadioInput'
import CheckboxInput from '../Atoms/CheckboxInput'
import { Button } from '@toasttab/shared/src/components'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import classNames from 'classnames'
import { FormStepStatementOptions } from '@toasttab/shared/src/utils/Contentful/richText'

export interface Field {
  title: string
  fieldLabel: string
  marketoFieldName: string
  isRequired: boolean
  fieldInputNameMarketoRestApi: string
  fieldType: string
  fieldOptions: { key: string; value: string }[] | []
  defaultValue?: string
  fieldDependentId?: string
  classes?: string
  __typename: string
}

export type CoreFieldsProps = Field | Statement

export interface Statement {
  title: string
  statement: any
  __typename: string
}

export interface FormStepProps {
  title: string
  coreFields: Field[] | Statement[]
  progressiveFields: Field[]
  submitLabel: string
  formik: any
  fieldIdPrefix?: string
  isSubmitting?: boolean
  isLastStep?: boolean
  isActive?: boolean
  componentContext?: 'ContentfulMdsShowcaseForm' | 'ContentfulMdsHero5050'
  color?: 'default' | 'inverse'
  layout?: 'default' | 'inline'
  region?: string
  intlBottomForm?: boolean
  usBottomForm?: boolean
}

const FormStep = ({
  title,
  coreFields,
  progressiveFields,
  submitLabel,
  formik,
  fieldIdPrefix,
  isSubmitting,
  isLastStep,
  isActive,
  componentContext = 'ContentfulMdsHero5050',
  color = 'default',
  layout = 'default',
  region = 'US',
  intlBottomForm,
  usBottomForm
}: FormStepProps) => {
  const [validating, setValidating] = useState(false)
  const fieldsetClasses = classNames({
    '[@media(min-width:576px)]:[&>div:nth-child(odd)>input]:mr-[5px] [@media(min-width:576px)]:[&>div:nth-child(even)>input]:ml-[5px] [@media(min-width:576px)]:[&>div:nth-child(even)>span]:ml-[5px] [&>div>label]:!right-[17px]':
      region === 'US' && !usBottomForm,
    '[@media(min-width:576px)]:[&>div:nth-child(even)>input]:mr-[5px] [@media(min-width:576px)]:[&>div:nth-child(odd)>input]:ml-[5px] [@media(min-width:576px)]:[&>div:nth-child(odd)>span]:ml-[5px]':
      region !== 'US' && !intlBottomForm,
    '[@media(min-width:992px)]:[&>div:nth-child(even)>input]:mr-[5px] [@media(min-width:992px)]:[&>div:nth-child(odd)>input]:ml-[5px] [@media(min-width:992px)]:[&>div:nth-child(odd)>span]:ml-[5px]':
      region !== 'US' && intlBottomForm,
    block: layout === 'default' && isActive,
    'flex flex-col md:flex-row gap-4 w-full md:items-center':
      layout === 'inline' && isActive,
    hidden: !isActive
  })

  const fillOptions = (
    fieldLabel: string,
    options: { key: string; value: string }[]
  ) => {
    const optionArr: { label: string; value: string; disabled: boolean }[] = [
      { label: fieldLabel, value: fieldLabel, disabled: true }
    ]
    options.map((o: { key: string; value: string }) => {
      return optionArr.push({
        label: o.key,
        value: o.value,
        disabled: false
      })
    })

    return optionArr
  }

  const renderFields = (fields: Field[] | Statement[]) => {
    return fields?.map((field: any, i: number) => {
      if (field.__typename === 'ContentfulMdsFormField') {
        const f = field as Field
        let fullWidthField =
          (f.fieldType !== 'Text' &&
            f.fieldType !== 'Email' &&
            f.fieldType !== 'Phone' &&
            (f.fieldType !== 'Select (Dropdown)' || region === 'US')) ||
          (f.marketoFieldName === 'mkoFullName' && region !== 'US') ||
          intlBottomForm ||
          usBottomForm ||
          f.marketoFieldName === 'toastType'

        if (
          f.fieldType === 'Select (Dropdown)' &&
          region === 'US' &&
          !usBottomForm
        ) {
          fullWidthField = false
        }
        return (
          <FormField
            classes={f.classes}
            fullWidth={fullWidthField}
            intlBottomForm={fullWidthField}
            key={i}
            smallerWidthFields={region !== 'US'}
          >
            {f.fieldType === 'Text' && (
              <TextInput
                fullWidth={fullWidthField}
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                padding='large'
                placeholder={f.fieldLabel}
                smallerWidthFields={region !== 'US'}
                type='text'
                value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                variation='default'
                error={
                  (validating ||
                    formik.touched[f.fieldInputNameMarketoRestApi]) &&
                  formik.errors[f.fieldInputNameMarketoRestApi]
                    ? (formik.errors[
                        f.fieldInputNameMarketoRestApi
                      ] as string[])
                    : ''
                }
              />
            )}
            {f.fieldType === 'Email' && (
              <TextInput
                fullWidth={fullWidthField}
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                padding='large'
                placeholder={f.fieldLabel}
                required={f.isRequired}
                smallerWidthFields={region !== 'US'}
                type='email'
                value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                variation='default'
                error={
                  (validating ||
                    formik.touched[f.fieldInputNameMarketoRestApi]) &&
                  formik.errors[f.fieldInputNameMarketoRestApi]
                    ? (formik.errors[
                        f.fieldInputNameMarketoRestApi
                      ] as string[])
                    : ''
                }
              />
            )}
            {f.fieldType === 'Phone' && (
              <TextInput
                fullWidth={fullWidthField}
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                padding='large'
                placeholder={f.fieldLabel}
                smallerWidthFields={region !== 'US'}
                type='tel'
                value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                variation='default'
                error={
                  (validating ||
                    formik.touched[f.fieldInputNameMarketoRestApi]) &&
                  formik.errors[f.fieldInputNameMarketoRestApi]
                    ? (formik.errors[
                        f.fieldInputNameMarketoRestApi
                      ] as string[])
                    : ''
                }
              />
            )}

            {f.fieldType === 'Select (Dropdown)' && (
              <SelectInput2
                defaultValue={f.defaultValue}
                fullWidth={fullWidthField}
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                options={fillOptions(f.fieldLabel, f.fieldOptions)}
                padding='large'
                required={true}
                error={
                  (validating ||
                    formik.touched[f.fieldInputNameMarketoRestApi]) &&
                  formik.errors[f.fieldInputNameMarketoRestApi]
                    ? (formik.errors[
                        f.fieldInputNameMarketoRestApi
                      ] as string[])
                    : ''
                }
              />
            )}
            {f.fieldType === 'Checkbox (Single/Multi Select)' && (
              <CheckboxInput
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onChange={formik.handleChange}
                region={region}
                textLeft={true}
                value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                variation='default'
              />
            )}
            {f.fieldType === 'Radio (Single-select)' && (
              <RadioInput
                id={`${fieldIdPrefix}-${f.marketoFieldName}`}
                label={f.fieldLabel}
                name={f.fieldInputNameMarketoRestApi}
                onChange={formik.handleChange}
                textLeft={true}
                value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                options={f.fieldOptions.map(
                  (o: { key: string; value: string }) => ({
                    label: o.key,
                    value: o.value
                  })
                )}
              />
            )}
          </FormField>
        )
      } else if (field.__typename === 'ContentfulMdsStatement') {
        // const f = field as Statement
        return (
          <React.Fragment key={i}>
            {renderRichText(field.statement, FormStepStatementOptions)}
          </React.Fragment>
        )
      }
    })
  }

  const handleNextStep = async () => {
    setValidating(true)
    const errors = await formik.validateForm(formik.values)

    if (Object.keys(errors).length === 0) {
      const formStep = isLastStep ? formik.values.step : formik.values.step + 1
      formik.setValues({ ...formik.values, step: formStep })
    } else {
      formik.setErrors(errors)
    }

    if (isLastStep) {
      if (validating) {
        formik.handleSubmit()
      }
    }
  }

  const titleVariation =
    componentContext === 'ContentfulMdsShowcaseForm' ? 'h3' : 'h2'

  return (
    <div className={fieldsetClasses}>
      {title ? (
        <Typography
          as={titleVariation}
          classes='mb-4'
          variation={titleVariation}
        >
          {title}
        </Typography>
      ) : null}
      {renderFields(coreFields)}
      <Button
        arrow={false}
        classes='mt-1'
        disabled={isSubmitting}
        fullwidth
        onClick={handleNextStep}
        size='medium'
        type={isLastStep ? 'submit' : 'button'}
        variation={color === 'inverse' ? 'secondary' : 'primary'}
      >
        {isSubmitting ? (
          <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
            <img
              alt='loadingspinner lazyload'
              className='w-[43px] h-[43px] grayscale'
              loading='lazy'
              src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
            />
          </div>
        ) : null}
        <div className={isSubmitting ? 'invisible' : 'font-semibold'}>
          {submitLabel}
        </div>
      </Button>
    </div>
  )
}

export default FormStep
